import {
  HistoricalUtilisationDto,
  KnownHistoricalUtilisationGrouping,
  KnownHistoricalUtilisationQueryDtoGroupBy
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { TooltipItem } from 'chart.js';
import dayjs from 'dayjs';
import _ from 'lodash';
import { ComponentProps } from 'react';
import { naturalSort } from '../../../core/util/natural-sort.util';
import { AreaChart, AreaChartProps } from '../../../ui/components/pure/charts/area/area-chart.component';
import { Colors } from '../../../ui/theme/colors';
import { lockerSizeToLockerIconColorMap, sortByLockerSize } from '../../lockers/logic/locker-ui.constants';
import { getDateLabelUnit, DateRange } from '../_shared/date-range';

export function makeLockerUtilisationChartProps(
  utilisationData: HistoricalUtilisationDto,
  range: DateRange
): Omit<AreaChartProps, 'isLoading'> {
  const sourceData = { ...utilisationData };
  switch (sourceData.metadata.groupBy as KnownHistoricalUtilisationQueryDtoGroupBy) {
    case KnownHistoricalUtilisationQueryDtoGroupBy.Hub:
    case KnownHistoricalUtilisationQueryDtoGroupBy.Locker:
      sourceData.groups = naturalSort(sourceData.groups).desc((el) => el.name);
      break;
    case KnownHistoricalUtilisationQueryDtoGroupBy.Size:
      sourceData.groups = sortByLockerSize(sourceData.groups, (el) => el.name);
      break;
    case KnownHistoricalUtilisationQueryDtoGroupBy.None:
      break;
  }
  const rawDatasets = makeLockerUtilisationDatasets(sourceData);
  const { gridColorCallback, tickCallback, labels: yAxisLabels, yAxisMax } = makeYAxisLabels(rawDatasets);
  const datasets = rawDatasets.map((d) => d.dataset);
  const granulatedDatasets = datasets.map((dataset) => ({
    ...dataset,
    data: granulateData(
      dataset.data,
      sourceData.metadata.fromDate,
      sourceData.metadata.toDate,
      getDataGranularity(range, sourceData.metadata.fromDate, sourceData.metadata.toDate)
    )
  }));
  // Important: This function should only be called after all other calculations have been made
  // because we don't want calculations to be made on the artificial data that is added to represent
  // changes in the total number of lockers available for each dataset
  generateLockerAvailabilityDatasets(granulatedDatasets, rawDatasets, sourceData);
  return {
    data: {
      datasets: granulatedDatasets,
      labels: getLabelCount(range, sourceData.metadata.fromDate, sourceData.metadata.toDate)
    },
    maxDate: sourceData.metadata.toDate.getTime(),
    dateLabelUnit: getDateLabelUnit(range, sourceData.metadata.toDate, sourceData.metadata.fromDate),
    yAxisLabelCallback: tickCallback,
    yAxisLabels,
    yAxisMax,
    tooltipLabelCallback: makeTooltipLabelCallback(granulatedDatasets, rawDatasets, sourceData),
    tooltipFilterCallback: makeTooltipFilterCallback(rawDatasets),
    yAxisGridColorCallback: gridColorCallback
  };
}

// The following functions construct the datasets to be provided to the chart.
// The main functionality of this logic is to normalize the data and to manipulate the values
// from each group to artificially stack each group on top of each other in the chart
// based on the maximum number of lockers available within each group

function makeLockerUtilisationDatasets(utilisationData: HistoricalUtilisationDto) {
  const datasets: { dataset: AreaChartProps['data']['datasets'][number]; maxAvailable: number }[] =
    utilisationData.groups.map((group) => {
      const data = makeLockerUtilisationData(group, utilisationData.metadata.fromDate, utilisationData.metadata.toDate);
      return {
        dataset: {
          label: group.name,
          borderWidth: 0,
          data
        },
        maxAvailable: getMaxTotalFromGroup(group)
      };
    });
  stackDatasets(datasets, utilisationData.metadata.groupBy);
  return datasets;
}

function getMaxTotalFromGroup(group: HistoricalUtilisationDto['groups'][number]) {
  return (
    _.maxBy([...group.events, group.currentState, group.initialState], (event) => event.total)?.total ||
    group.currentState.total
  );
}

function makeLockerUtilisationData(group: HistoricalUtilisationDto['groups'][number], fromDate: Date, toDate: Date) {
  const data: { x: number; y: number }[] = [
    { x: fromDate.getTime(), y: group.initialState.inUse + group.initialState.reserved },
    ...group.events.map((event) => ({
      x: event.timestamp.getTime(),
      y: event.inUse + event.reserved
    }))
  ];
  const maxDate = _.maxBy(data, (el) => el.x)?.y || 0;
  if (maxDate <= toDate.getTime()) {
    data.push({ x: toDate.getTime(), y: group.currentState.inUse + group.currentState.reserved });
  }
  return data;
}

function stackDatasets(
  datasets: { dataset: AreaChartProps['data']['datasets'][number]; maxAvailable: number }[],
  groupBy: HistoricalUtilisationDto['metadata']['groupBy']
) {
  let prevMax = 0;

  datasets.forEach((dataset, idx) => {
    dataset.dataset.data.forEach((el) => {
      el.y += prevMax;
    });
    if (idx === 0) {
      dataset.dataset.fill = true;
      dataset.dataset.backgroundColor = getDatasetBackgroundColor(groupBy, dataset.dataset.label, idx);
    } else {
      dataset.dataset.fill = {
        target: { value: prevMax },
        above: getDatasetBackgroundColor(groupBy, dataset.dataset.label, idx)
      };
      dataset.dataset.backgroundColor = getDatasetBackgroundColor(groupBy, dataset.dataset.label, idx);
    }
    prevMax += dataset.maxAvailable;
  });
}

// This function inspects the original data to find any instances when the total number of lockers
// is/was less than the maximum possible total number of lockers across the date range for each data group.
// It then constructs artificial data which will be drawn on the chart in a grey block that
// denotes that at the given period of time, the total number of lockers is/was less than the maximum possible
function generateLockerAvailabilityDatasets(
  datasets: AreaChartProps['data']['datasets'],
  rawDatasets: { dataset: AreaChartProps['data']['datasets'][number]; maxAvailable: number }[],
  utilisationData: HistoricalUtilisationDto
) {
  for (const [datasetIndex, group] of utilisationData.groups.entries()) {
    let nextData: { x: number; y: number }[] = [];

    const currentMaxAggregate = rawDatasets.reduce((acc, dataset, idx) => {
      if (idx <= datasetIndex) {
        return acc + dataset.maxAvailable;
      } else {
        return acc;
      }
    }, 0);
    const groupMaxTotal = getMaxTotalFromGroup(group);

    let currentDiff = group.initialState.total - groupMaxTotal;

    if (currentDiff < 0) {
      nextData.push({ x: utilisationData.metadata.fromDate.getTime(), y: currentMaxAggregate });
    }

    for (const event of group.events) {
      const nextDiff = event.total - groupMaxTotal;
      if (nextDiff === currentDiff) {
        continue;
      }
      if (currentDiff !== 0) {
        nextData.push({ x: event.timestamp.getTime(), y: currentMaxAggregate });
        datasets.push({
          data: [...nextData],
          fill: { target: { value: currentDiff + currentMaxAggregate }, above: 'rgb(196,195,195)' }
        });
        nextData = [];
      }
      if (nextDiff !== 0) {
        nextData.push({ x: event.timestamp.getTime(), y: currentMaxAggregate });
      }
      currentDiff = nextDiff;
    }

    const finalDiff = group.currentState.total - groupMaxTotal;

    if (currentDiff === finalDiff && currentDiff !== 0) {
      nextData.push({ x: utilisationData.metadata.toDate.getTime(), y: currentMaxAggregate });
      datasets.push({
        data: [...nextData],
        fill: { target: { value: currentDiff + currentMaxAggregate }, above: 'rgb(196,195,195)' }
      });
    }
  }
}

function getDatasetBackgroundColor(
  groupBy: HistoricalUtilisationDto['metadata']['groupBy'],
  name: string | undefined,
  idx: number
) {
  switch (groupBy) {
    case KnownHistoricalUtilisationGrouping.None:
      return Colors.collectedGreen;
    case KnownHistoricalUtilisationGrouping.Size:
      return name ? lockerSizeToLockerIconColorMap(name) : getBrandColorByIndex(idx);
    default:
      return getBrandColorByIndex(idx);
  }
}

export function getBrandColorByIndex(idx: number): string {
  if (idx < brandColorsList.length) {
    return brandColorsList[idx];
  } else {
    const nextIdx = idx - brandColorsList.length;
    return getBrandColorByIndex(nextIdx);
  }
}

const brandColorsList = [
  'rgb(106, 51, 145)',
  'rgb(219, 46, 45)',
  'rgb(51, 175, 167)',
  'rgb(230, 116, 2)',
  'rgb(255, 199, 0)',
  'rgb(11, 68, 135)'
];

// Logic for adding granularity to the data based on date range
// This causes the shaded area on the chart to more accurately reflect
// locker utilisation at a more granular time scale than the data provides

function granulateData(
  data: AreaChartProps['data']['datasets'][number]['data'],
  fromDate: Date,
  toDate: Date,
  granularity: number
) {
  const realValues = data.map((el) => ({ ...el, real: true }));
  const initialValue = fromDate.getTime() + granularity;
  const finalValue = toDate.getTime();
  const arrLen = Math.floor((finalValue - initialValue) / granularity);
  const fauxValues = Array(arrLen)
    .fill('')
    .map((_, idx) => ({ x: idx * granularity + initialValue, y: 0, real: false }));
  const all = [...fauxValues, ...realValues].sort((a, b) => a.x - b.x);
  const final: { x: number; y: number }[] = [];
  for (const [idx, el] of all.entries()) {
    if (el.real) {
      final.push({ x: el.x, y: el.y });
    } else {
      final.push({
        x: el.x,
        y: final[idx - 1].y
      });
    }
  }
  return final;
}

function getDataGranularity(range: DateRange, fromDate: Date, toDate: Date) {
  const minute = 60000;
  const hour = minute * 60;
  const day = hour * 24;

  switch (range) {
    case DateRange.Day:
      return 10 * minute;
    case DateRange.Week:
      return 30 * minute;
    case DateRange.Month:
      return 6 * hour;
    case DateRange.AllTime:
      const duration = dayjs(toDate).diff(fromDate, 'day');
      if (duration <= 1) {
        return minute;
      } else if (duration <= 31) {
        return hour;
      } else {
        return day;
      }
    default:
      return day;
  }
}

function getLabelCount(range: DateRange, fromDate: Date, toDate: Date) {
  let granularity: 'hour' | 'day' | 'month';

  if (range === DateRange.Day) {
    granularity = 'hour';
  } else if (range === DateRange.Week || range === DateRange.Month) {
    granularity = 'day';
  } else if (range === DateRange.HalfYear || range === DateRange.Year) {
    granularity = 'month';
  } else {
    // AllTime
    const duration = dayjs(toDate).diff(fromDate, 'day');
    if (duration <= 1) {
      granularity = 'hour';
    } else if (duration <= 31) {
      granularity = 'day';
    } else {
      granularity = 'month';
    }
  }

  const from = dayjs(fromDate).startOf(granularity).toDate().getTime();
  const to = dayjs(toDate).endOf(granularity).toDate().getTime();
  const labels = [from];
  let done = false;
  let next = from;
  while (!done) {
    next = dayjs(next).add(1, granularity).toDate().getTime();
    if (next < to) {
      labels.push(next);
    } else {
      labels.push(to);
      done = true;
    }
  }
  return labels;
}

// This factory function returns the callback that is passed into the tooltip options of the chart
// that reverts the artificial value of y back to its original value to be displayed in the tooltip
function makeTooltipLabelCallback(
  datasets: AreaChartProps['data']['datasets'],
  rawDatasets: { dataset: AreaChartProps['data']['datasets'][number]; maxAvailable: number }[],
  utilisationData: HistoricalUtilisationDto
) {
  return function (tooltipItem: TooltipItem<'line'>) {
    const defaultValue = `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
    const datasetIndex = tooltipItem.datasetIndex;
    const dataset = datasets[datasetIndex];
    if (!dataset) {
      return defaultValue;
    }
    const thisData = dataset.data[tooltipItem.dataIndex];
    const thisSourceData = utilisationData.groups[datasetIndex];
    if (!thisData || !thisSourceData) {
      return defaultValue;
    }
    // find event with timestamp that is after but closest to thisTimestamp
    const thisTimestamp = thisData.x;
    const eventsByClosestMatch = [
      { ...thisSourceData.initialState, timestamp: utilisationData.metadata.fromDate },
      ...thisSourceData.events,
      { ...thisSourceData.currentState, timestamp: utilisationData.metadata.toDate }
    ].sort((a, b) => {
      if (b.timestamp.getTime() > thisTimestamp) {
        return -1;
      }
      if (a.timestamp.getTime() > thisTimestamp) {
        return 1;
      }
      const distanceFromA = thisTimestamp - a.timestamp.getTime();
      const distanceFromB = thisTimestamp - b.timestamp.getTime();

      return distanceFromA - distanceFromB;
    });
    const thisMax = eventsByClosestMatch[0]?.total || 0;
    const prevMaxAggregate =
      datasetIndex === 0
        ? 0
        : rawDatasets.reduce((acc, dataset, idx) => {
            if (idx < datasetIndex) {
              return acc + dataset.maxAvailable;
            } else {
              return acc;
            }
          }, 0);
    const rawValue = parseFloat(tooltipItem.formattedValue);
    const actualValue = rawValue - prevMaxAggregate;
    return `${tooltipItem.dataset.label}: ${actualValue.toFixed()} of ${thisMax.toFixed()}`;
  };
}

// This factory function returns the callback that is passed to the chart tooltip filter so that the
// tooltip does not appear when:
//  1) the cursor hovers over any artificial datasets created for the purpose of denoting differences in the total number of lockers
//  2) the dataset has a maxAvailable of 0, meaning it has no lockers
function makeTooltipFilterCallback(
  datasets: { dataset: AreaChartProps['data']['datasets'][number]; maxAvailable: number }[]
) {
  return function (...args: Parameters<Exclude<ComponentProps<typeof AreaChart>['tooltipFilterCallback'], undefined>>) {
    const [tooltipItem] = args;
    const dataset = datasets[tooltipItem.datasetIndex];
    if (dataset && dataset.maxAvailable === 0) {
      return false;
    }
    return tooltipItem.datasetIndex < datasets.length;
  };
}

// This factory function returns the callbacks that are passed into the yAxis scale of the chart
// which the chart uses to determine which grid lines to show and which to put labels on
function makeYAxisLabels(datasets: { dataset: AreaChartProps['data']['datasets'][number]; maxAvailable: number }[]) {
  let prevMax = 0;
  const labels: (string | number)[] = [
    0,
    ...datasets
      .map(({ maxAvailable }) => {
        const min = prevMax;
        const max = maxAvailable + prevMax;
        const middle = (max - min) / 2 + min;
        prevMax = max;
        return [middle, max];
      })
      .flat()
  ];

  function tickCallback(
    ...args: Parameters<Exclude<ComponentProps<typeof AreaChart>['yAxisLabelCallback'], undefined>>
  ) {
    const [tickValue] = args;
    const idx = labels.indexOf(tickValue);
    if (idx !== -1) {
      if (idx % 2 === 0) {
        return '';
      }
      const datasetIndex = (idx - 1) / 2;
      const label = datasets[datasetIndex]?.dataset.label;
      return label || null;
    }
    return null;
  }

  function gridColorCallback(
    ...args: Parameters<Exclude<ComponentProps<typeof AreaChart>['yAxisGridColorCallback'], undefined>>
  ) {
    const [ctx] = args;

    const idx = labels.indexOf(ctx.tick.value);
    if (idx !== -1) {
      if (idx % 2 === 0) {
        return 'rgba(0, 0, 0, 0.1)';
      }
      return 'transparent';
    }
    return undefined;
  }

  return { labels, tickCallback, gridColorCallback, yAxisMax: prevMax };
}
